/**
 * Error categorization utility for retry logic
 * Follows ADR-003: High Cohesion - Single responsibility: classify errors
 */

/**
 * Error categories for retry logic
 */
export const ErrorCategory = {
  /** Permanent errors - don't retry */
  PERMANENT: 'permanent',
  /** Transient errors - retry with backoff */
  TRANSIENT: 'transient',
  /** Rate limited - retry with longer delay */
  RATE_LIMITED: 'rate_limited'
} as const;

export type ErrorCategory = typeof ErrorCategory[keyof typeof ErrorCategory];

/**
 * Result of error categorization
 */
export interface ErrorCategoryResult {
  category: ErrorCategory;
  statusCode?: number;
}

/**
 * Extracts status code and message from an error
 * @param error - The error to extract from
 * @returns Extracted status code and message
 */
function extractErrorInfo(error: unknown): { statusCode?: number; message: string } {
  let statusCode: number | undefined;
  let errorMessage = '';

  if (error && typeof error === 'object') {
    if ('status' in error && typeof (error as { status?: unknown }).status === 'number') {
      statusCode = (error as { status: number }).status;
    } else if ('statusCode' in error && typeof (error as { statusCode?: unknown }).statusCode === 'number') {
      statusCode = (error as { statusCode: number }).statusCode;
    }
    
    if ('message' in error) {
      errorMessage = String((error as { message?: unknown }).message || '');
    }
  }

  if (error instanceof Error) {
    errorMessage = error.message;
    
    // Try to extract status code from error message
    const statusMatch = errorMessage.match(/\b(40[0-9]|50[0-9]|429)\b/);
    if (statusMatch && !statusCode) {
      statusCode = parseInt(statusMatch[1], 10);
    }
  }

  return { statusCode, message: errorMessage };
}

/**
 * Checks if error message matches permanent error patterns
 */
function matchesPermanentPattern(message: string): { matches: boolean; statusCode?: number } {
  const lower = message.toLowerCase();
  
  // Session/Agent/Resource not found - permanent errors (resource doesn't exist)
  if (lower.includes('session not found') ||
      lower.includes('session may have been deleted') ||
      lower.includes('agent not found') || 
      lower.includes('agent id') ||
      lower.includes('resource not found') ||
      lower.includes('customer not found')) {
    return { matches: true, statusCode: 404 };
  }
  
  // Access denied
  if (lower.includes('403') || lower.includes('access denied') || 
      lower.includes('forbidden') || lower.includes('permission')) {
    return { matches: true, statusCode: 403 };
  }
  
  // Authentication failed
  if (lower.includes('401') || lower.includes('authentication failed') || 
      lower.includes('unauthorized') ||
      (lower.includes('invalid') && lower.includes('token'))) {
    return { matches: true, statusCode: 401 };
  }
  
  return { matches: false };
}

/**
 * Checks if error message matches rate limiting patterns
 */
function matchesRateLimitPattern(message: string): boolean {
  const lower = message.toLowerCase();
  return lower.includes('429') || 
         lower.includes('too many requests') || 
         lower.includes('rate limit');
}

/**
 * Checks if error message matches transient error patterns
 */
function matchesTransientPattern(message: string): boolean {
  const lower = message.toLowerCase();
  
  // Server errors
  if (lower.includes('500') || lower.includes('502') || 
      lower.includes('503') || lower.includes('504') ||
      lower.includes('internal server error') || lower.includes('bad gateway') || 
      lower.includes('service unavailable') || lower.includes('gateway timeout')) {
    return true;
  }
  
  // Network/connection errors
  if (lower.includes('network') || lower.includes('connection') || 
      lower.includes('timeout') || lower.includes('fetch failed') || 
      lower.includes('failed to fetch') || lower.includes('server unavailable')) {
    return true;
  }
  
  return false;
}

/**
 * Categorizes an error to determine retry strategy
 * @param error - The error to categorize
 * @returns Error category and status code if available
 */
export function categorizeError(error: unknown): ErrorCategoryResult {
  const { statusCode, message } = extractErrorInfo(error);
  
  // Check message patterns FIRST (catches errors without status codes)
  const permanentMatch = matchesPermanentPattern(message);
  if (permanentMatch.matches) {
    return { 
      category: ErrorCategory.PERMANENT, 
      statusCode: statusCode || permanentMatch.statusCode 
    };
  }
  
  if (matchesRateLimitPattern(message)) {
    return { category: ErrorCategory.RATE_LIMITED, statusCode: 429 };
  }
  
  if (matchesTransientPattern(message)) {
    return { category: ErrorCategory.TRANSIENT, statusCode: statusCode || 500 };
  }

  // Check status code if message patterns didn't match
  if (statusCode !== undefined) {
    if (statusCode === 401 || statusCode === 403) {
      return { category: ErrorCategory.PERMANENT, statusCode };
    }
    
    if (statusCode === 404) {
      // All 404 errors are permanent - the resource doesn't exist
      // Session not found, agent not found, customer not found - none will magically appear
      return { category: ErrorCategory.PERMANENT, statusCode };
    }
    
    if (statusCode === 429) {
      return { category: ErrorCategory.RATE_LIMITED, statusCode };
    }
    
    if (statusCode >= 500 && statusCode <= 504) {
      return { category: ErrorCategory.TRANSIENT, statusCode };
    }
    
    if (statusCode >= 400 && statusCode < 500) {
      return { category: ErrorCategory.PERMANENT, statusCode };
    }
  }

  // Default to PERMANENT for unknown errors to avoid retry loops
  return { category: ErrorCategory.PERMANENT, statusCode };
}

